<script setup lang="ts">
import { useStorage } from '@vueuse/core';
import { STORAGE_KEY_BANNER } from '~/utilities/constants';
import { useSystemStore } from '~/stores/system-store';
import IconClose from '~icons/carbon/close';

const dismissedBanners = useStorage(STORAGE_KEY_BANNER, new Set<string>());

function dismissBanner(bannerId: string) {
  dismissedBanners.value.add(bannerId);
}

function isBannerDismissed(bannerId: string) {
  return dismissedBanners.value.has(bannerId);
}

const systemStore = useSystemStore();
</script>

<template>
  <template v-for="banner in systemStore.banners" :key="banner.id">
    <div
      :key="banner.id"
      v-if="!isBannerDismissed(banner.id)"
      id="notification-banner"
      class="relative flex shrink-0 items-center justify-center overflow-hidden border-b border-b-black/10"
      :class="banner.level"
    >
      <div
        class="mx-auto px-8 py-2.5 text-center text-base font-semibold italic leading-tight text-new-content-900"
      >
        {{ banner.message }}
        <a v-if="banner.url" :href="banner.url" class="underline" @click="dismissBanner(banner.id)"
          >Click for details</a
        >
      </div>

      <button class="absolute right-2 top-1/2 -translate-y-1/2" @click="dismissBanner(banner.id)">
        <IconClose class="text-lg text-white md:text-2xl" />
      </button>
    </div>
  </template>
</template>

<style scoped lang="postcss">
.laptop {
  transform-origin: bottom center;
  position: absolute;
  bottom: 5px;
  font-size: 22px;
  transition-property: transform;
  transition-timing-function: cubic-bezier(0, 0, 0.2, 1);
  transition-duration: 200ms;
}

.info {
  /**
   * https://www.joshwcomeau.com/gradient-generator?colors=63c97b|31a824|63c97b|31a824|63c97b&angle=225&colorMode=hsv&precision=13&easingCurve=0.25|0.75|0.75|0.25
   */
  background-image: linear-gradient(
    225deg,
    hsl(134deg 49% 59%) 0%,
    hsl(129deg 47% 54%) 6%,
    hsl(125deg 47% 50%) 12%,
    hsl(120deg 54% 45%) 18%,
    hsl(115deg 63% 41%) 24%,
    hsl(118deg 58% 43%) 29%,
    hsl(122deg 51% 47%) 35%,
    hsl(127deg 47% 52%) 41%,
    hsl(132deg 48% 56%) 47%,
    hsl(132deg 48% 56%) 53%,
    hsl(127deg 47% 52%) 59%,
    hsl(122deg 51% 47%) 65%,
    hsl(118deg 58% 43%) 71%,
    hsl(115deg 63% 41%) 76%,
    hsl(120deg 54% 45%) 82%,
    hsl(125deg 47% 50%) 88%,
    hsl(129deg 47% 54%) 94%,
    hsl(134deg 49% 59%) 100%
  );

  &:hover {
    .laptop {
      transform: perspective(70px) rotateX(-50deg);
    }
  }
}

.warning {
  background-image: linear-gradient(
    225deg,
    hsl(48deg 49% 59%) 0%,
    hsl(51deg 47% 54%) 6%,
    hsl(53deg 47% 50%) 12%,
    hsl(55deg 54% 45%) 18%,
    hsl(58deg 63% 41%) 24%,
    hsl(57deg 58% 43%) 29%,
    hsl(56deg 51% 47%) 35%,
    hsl(55deg 47% 52%) 41%,
    hsl(54deg 48% 56%) 47%,
    hsl(54deg 48% 56%) 53%,
    hsl(55deg 47% 52%) 59%,
    hsl(57deg 51% 47%) 65%,
    hsl(59deg 58% 43%) 71%,
    hsl(60deg 63% 41%) 76%,
    hsl(61deg 54% 45%) 82%,
    hsl(61deg 47% 50%) 88%,
    hsl(61deg 47% 54%) 94%,
    hsl(62deg 49% 59%) 100%
  );
}

.error {
  background-image: linear-gradient(
    225deg,
    hsl(0deg 49% 59%) 0%,
    hsl(0deg 47% 54%) 6%,
    hsl(0deg 47% 50%) 12%,
    hsl(0deg 54% 45%) 18%,
    hsl(0deg 63% 41%) 24%,
    hsl(0deg 58% 43%) 29%,
    hsl(0deg 51% 47%) 35%,
    hsl(0deg 47% 52%) 41%,
    hsl(0deg 48% 56%) 47%,
    hsl(0deg 48% 56%) 53%,
    hsl(0deg 47% 52%) 59%,
    hsl(0deg 51% 47%) 65%,
    hsl(0deg 58% 43%) 71%,
    hsl(0deg 63% 41%) 76%,
    hsl(0deg 54% 45%) 82%,
    hsl(0deg 47% 50%) 88%,
    hsl(0deg 47% 54%) 94%,
    hsl(0deg 49% 59%) 100%
  );
}
</style>
